import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { getAllLeads } from '../../services/leadService';
import { getAllPayments } from '../../services/paymentService';
import { Lead } from '../../models/Lead';
import { Payment } from '../../models/Payment';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface AnalyticsData {
    totalLeads: number;
    totalSearches: number;
    responseRate: number;
    conversionRate: number;
    avgDaysToConversion: number;
    leadsByMonth: { month: string; count: number }[];
    conversionBySource: { source: string; count: number }[];
    leadsByLocation: { location: string; count: number }[];
    leadsByIndustry: { industry: string; count: number }[];
    totalRevenue: number;
    totalProfit: number;
    revenueByMonth: { month: string; revenue: number; profit: number }[];
    revenueByType: { type: string; revenue: number; profit: number }[];
}

const AnalyticsComponent: React.FC = () => {
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
        totalLeads: 0,
        totalSearches: 0,
        responseRate: 0,
        conversionRate: 0,
        avgDaysToConversion: 0,
        leadsByMonth: [],
        conversionBySource: [],
        leadsByLocation: [],
        leadsByIndustry: [],
        totalRevenue: 0,
        totalProfit: 0,
        revenueByMonth: [],
        revenueByType: []
    });

    const [filters, setFilters] = useState({
        location: '',
        industry: '',
        dateRange: 'all'
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchAnalyticsData();
    }, [filters]);

    const fetchAnalyticsData = async () => {
        try {
            // Get all leads and payments
            const [leads, payments] = await Promise.all([
                getAllLeads(),
                getAllPayments()
            ]);

            // Apply filters
            let filteredLeads = leads;
            if (filters.location) {
                filteredLeads = filteredLeads.filter(lead => lead.city === filters.location);
            }
            if (filters.industry) {
                filteredLeads = filteredLeads.filter(lead => lead.industryType === filters.industry);
            }

            // Calculate lead analytics
            const totalLeads = filteredLeads.length;
            const totalSearches = filteredLeads.filter(lead => lead.source === 'search').length;
            const responseRate = (filteredLeads.filter(lead => lead.responseReceived).length / totalLeads) * 100;
            const conversionRate = (filteredLeads.filter(lead => lead.convertedToOpportunity).length / totalLeads) * 100;

            // Calculate average days to conversion
            const convertedLeads = filteredLeads.filter(lead => lead.convertedToOpportunity && lead.convertedAt);
            const totalDays = convertedLeads.reduce((sum, lead) => {
                if (lead.convertedAt && lead.createdAt) {
                    const days = Math.floor((lead.convertedAt.getTime() - lead.createdAt.getTime()) / (1000 * 60 * 60 * 24));
                    return sum + days;
                }
                return sum;
            }, 0);
            const avgDaysToConversion = convertedLeads.length > 0 ? totalDays / convertedLeads.length : 0;

            // Calculate financial metrics
            const totalRevenue = payments.reduce((sum, payment) => sum + payment.amount, 0);
            const totalProfit = payments.reduce((sum, payment) => sum + (payment.amount - (payment.cost || 0)), 0);

            // Group data by month
            const leadsByMonth = groupLeadsByMonth(filteredLeads);
            const revenueByMonth = groupRevenueByMonth(payments);

            // Group data by type/source
            const conversionBySource = groupConversionsBySource(filteredLeads);
            const revenueByType = groupRevenueByType(payments);

            // Group leads by location and industry
            const leadsByLocation = groupLeadsByLocation(filteredLeads);
            const leadsByIndustry = groupLeadsByIndustry(filteredLeads);

            setAnalyticsData({
                totalLeads,
                totalSearches,
                responseRate,
                conversionRate,
                avgDaysToConversion,
                leadsByMonth,
                conversionBySource,
                leadsByLocation,
                leadsByIndustry,
                totalRevenue,
                totalProfit,
                revenueByMonth,
                revenueByType
            });
        } catch (error) {
            console.error('Error fetching analytics data:', error);
        }
    };

    const groupLeadsByMonth = (leads: Lead[]) => {
        const monthCounts: { [key: string]: number } = {};
        leads.forEach(lead => {
            const month = lead.createdAt.toLocaleString('default', { month: 'short', year: 'numeric' });
            monthCounts[month] = (monthCounts[month] || 0) + 1;
        });
        return Object.entries(monthCounts).map(([month, count]) => ({ month, count }));
    };

    const groupRevenueByMonth = (payments: Payment[]) => {
        const monthData: { [key: string]: { revenue: number; profit: number } } = {};
        payments.forEach(payment => {
            const month = payment.createdAt.toLocaleString('default', { month: 'short', year: 'numeric' });
            if (!monthData[month]) {
                monthData[month] = { revenue: 0, profit: 0 };
            }
            monthData[month].revenue += payment.amount;
            monthData[month].profit += payment.amount - (payment.cost || 0);
        });
        return Object.entries(monthData).map(([month, data]) => ({
            month,
            revenue: data.revenue,
            profit: data.profit
        }));
    };

    const groupRevenueByType = (payments: Payment[]) => {
        const typeData: { [key: string]: { revenue: number; profit: number } } = {};
        payments.forEach(payment => {
            if (!typeData[payment.type]) {
                typeData[payment.type] = { revenue: 0, profit: 0 };
            }
            typeData[payment.type].revenue += payment.amount;
            typeData[payment.type].profit += payment.amount - (payment.cost || 0);
        });
        return Object.entries(typeData).map(([type, data]) => ({
            type,
            revenue: data.revenue,
            profit: data.profit
        }));
    };

    const groupConversionsBySource = (leads: Lead[]) => {
        const sourceCounts: { [key: string]: number } = {};
        leads.filter(lead => lead.convertedToOpportunity).forEach(lead => {
            const source = lead.conversionSource || 'unknown';
            sourceCounts[source] = (sourceCounts[source] || 0) + 1;
        });
        return Object.entries(sourceCounts).map(([source, count]) => ({ source, count }));
    };

    const groupLeadsByLocation = (leads: Lead[]) => {
        const locationCounts: { [key: string]: number } = {};
        leads.forEach(lead => {
            const location = lead.city || 'Unknown';
            locationCounts[location] = (locationCounts[location] || 0) + 1;
        });
        return Object.entries(locationCounts).map(([location, count]) => ({ location, count }));
    };

    const groupLeadsByIndustry = (leads: Lead[]) => {
        const industryCounts: { [key: string]: number } = {};
        leads.forEach(lead => {
            const industry = lead.industryType || 'Unknown';
            industryCounts[industry] = (industryCounts[industry] || 0) + 1;
        });
        return Object.entries(industryCounts).map(([industry, count]) => ({ industry, count }));
    };

    const handleFilterChange = (event: SelectChangeEvent<string>) => {
        setFilters(prev => ({
            ...prev,
            [event.target.name]: event.target.value
        }));
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Analytics Dashboard
            </Typography>

            {/* Filters */}
            <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Location</InputLabel>
                    <Select
                        name="location"
                        value={filters.location}
                        label="Location"
                        onChange={handleFilterChange}
                    >
                        <MenuItem value="">All Locations</MenuItem>
                        {analyticsData.leadsByLocation.map(item => (
                            <MenuItem key={item.location} value={item.location}>
                                {item.location}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Industry</InputLabel>
                    <Select
                        name="industry"
                        value={filters.industry}
                        label="Industry"
                        onChange={handleFilterChange}
                    >
                        <MenuItem value="">All Industries</MenuItem>
                        {analyticsData.leadsByIndustry.map(item => (
                            <MenuItem key={item.industry} value={item.industry}>
                                {item.industry}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6">Total Leads</Typography>
                        <Typography variant="h4">{analyticsData.totalLeads}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6">Total Revenue</Typography>
                        <Typography variant="h4">${analyticsData.totalRevenue.toLocaleString()}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6">Total Profit</Typography>
                        <Typography variant="h4">${analyticsData.totalProfit.toLocaleString()}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6">Conversion Rate</Typography>
                        <Typography variant="h4">{analyticsData.conversionRate.toFixed(1)}%</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Financial Tables */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Revenue by Payment Type</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Payment Type</TableCell>
                                        <TableCell align="right">Revenue</TableCell>
                                        <TableCell align="right">Profit</TableCell>
                                        <TableCell align="right">Profit Margin</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analyticsData.revenueByType
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.type}>
                                                <TableCell component="th" scope="row">
                                                    {row.type}
                                                </TableCell>
                                                <TableCell align="right">${row.revenue.toLocaleString()}</TableCell>
                                                <TableCell align="right">${row.profit.toLocaleString()}</TableCell>
                                                <TableCell align="right">
                                                    {((row.profit / row.revenue) * 100).toFixed(1)}%
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={analyticsData.revenueByType.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3}>
                {/* Revenue Over Time */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Revenue Over Time</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={analyticsData.revenueByMonth}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Revenue" />
                                <Line type="monotone" dataKey="profit" stroke="#82ca9d" name="Profit" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Revenue by Type */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Revenue by Type</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={analyticsData.revenueByType}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="type" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="revenue" fill="#8884d8" name="Revenue" />
                                <Bar dataKey="profit" fill="#82ca9d" name="Profit" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Leads Over Time */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Leads Over Time</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={analyticsData.leadsByMonth}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="count" stroke="#8884d8" name="Leads" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Conversions by Source */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Conversions by Source</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={analyticsData.conversionBySource}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="source" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" fill="#82ca9d" name="Conversions" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default AnalyticsComponent; 